import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  SOURCE_ADMIN_SMS,
  SOURCE_RCS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import { useFlag } from '@rsos/flags';
import { StreamWSMessage } from '@rsos/hermes-js/src/typings/common';
import {
  CONVERSATION_END,
  CONVERSATION_START,
  CONVERSATION_UPDATE,
  ConversationMessage,
} from '@rsos/hermes-js/src/typings/conversation';
import {
  addConversation,
  updateConversation,
} from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import { addRCSConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSlice';
import { dismissToastByID } from '@rsos/utils/toastUtils';
import { addLocToQueue } from '../../../actions';
import useHasAccessToAdminLineRequests from '../../../hooks/useHasAccessToAdminLineRequests';
import { convertKeysToCamelCase } from './helpers';

const conversationStatuses = new Set([
  CONVERSATION_START,
  CONVERSATION_UPDATE,
  CONVERSATION_END,
]);

const normalizeLocation = (body: ConversationMessage['body']) => {
  const baseLocation = {
    caller_id: body.caller_id,
    call_start_time: body.conversation_start_time,
    source: body.source,
  };

  if ('location_data' in body) {
    return {
      ...baseLocation,
      ...body.location_data,
    };
  }

  return baseLocation;
};

const isConversationMessage = (
  message: StreamWSMessage,
): message is ConversationMessage => {
  return conversationStatuses.has(message.type);
};

const useHandleConversationMessage = () => {
  const StreamWS = window?.__RSOS?.streamWS?.instance;
  const dispatch = useDispatch();
  const hasAdminLineRequests = useHasAccessToAdminLineRequests();
  const isTCAPIStreamWS = useFlag('tcapi_streamWS');

  const handleConversationMessage = useCallback(
    (message: StreamWSMessage) => {
      if (isConversationMessage(message)) {
        const body = message.body;
        const source = body.source;
        const isUnAuthAdmin =
          source === SOURCE_ADMIN_SMS && !hasAdminLineRequests;
        const isTCAPINotAllowed = source === SOURCE_TCAPI && !isTCAPIStreamWS;

        if (isUnAuthAdmin || isTCAPINotAllowed) {
          return;
        }

        const normalizedLocation = normalizeLocation(body);
        dispatch(addLocToQueue(normalizedLocation));

        let payload = body;
        if (source === SOURCE_TCAPI) {
          const snakeCaseExtraInfo = convertKeysToCamelCase(body.extra_info);
          payload = { ...body, ...snakeCaseExtraInfo };

          if (message.type === CONVERSATION_UPDATE) {
            dismissToastByID(`conversations-toast-${body.caller_id}`);
          }
        }

        if (source === SOURCE_RCS) {
          dispatch(addRCSConversation(body.session_metadata));
        } else if (message.type === CONVERSATION_START) {
          dispatch(addConversation(payload));
        } else if (
          message.type === CONVERSATION_UPDATE ||
          message.type === CONVERSATION_END
        ) {
          dispatch(
            updateConversation({ body: payload, messageType: message.type }),
          );
        }
      }
    },
    [dispatch, hasAdminLineRequests, isTCAPIStreamWS],
  );

  useEffect(() => {
    if (StreamWS) {
      StreamWS.addOnMessageCallback({
        handleConversationMessage: handleConversationMessage as unknown as (
          message: Record<string, unknown>,
        ) => void,
      });

      return () => {
        StreamWS.removeOnMessageCallback('handleConversationMessage');
      };
    }
  }, [StreamWS, handleConversationMessage]);
};

export default useHandleConversationMessage;
